/**
 * Application Insights bootstrap for the marketplace SPA.
 *
 * - Reads the connection string from `import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING`.
 *   When unset (local dev, preview deploys without staging conn string), every
 *   export is a silent no-op — local dev is the common path, so we do not warn.
 * - Idempotent: `initAppInsights()` is safe to call twice (subsequent calls
 *   are no-ops). Global `error` + `unhandledrejection` listeners are installed
 *   exactly once across the page lifetime.
 * - Distributed-trace correlation (T6): W3C `traceparent` mode + CORS-friendly
 *   header tracking, scoped to the configured API host.
 * - Breadcrumb attach (T7): `trackException` serialises a snapshot of the
 *   route + fetch ring buffers onto the AI envelope, with a 4 KB cap.
 *
 * The bundle stamps `cloud.role='marketplace-ui'` and
 * `application.ver=__APP_VERSION__` (Vite `define` constant — see
 * `vite.config.ts`) onto every envelope so KQL can pivot on
 * `cloud_RoleName == 'marketplace-ui'` and join SPA exceptions back to a
 * specific release.
 */

import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import {
  ApplicationInsights,
  DistributedTracingModes,
  type ITelemetryItem,
} from '@microsoft/applicationinsights-web';
import { snapshot } from './breadcrumbs';
import { notifyTelemetryDisabled } from './notify-telemetry-disabled';

/**
 * Max serialised breadcrumb payload before we start dropping oldest entries.
 * Cap is measured in JS chars, not UTF-8 bytes; the AI envelope counts chars
 * for size limits.
 */
const BREADCRUMB_CHAR_CAP = 4 * 1024;

/** ReactPlugin instance — exported so `App.tsx` / route effects can read it. */
export const reactPlugin = new ReactPlugin();

/**
 * The live AI client, or `null` when no conn string is configured. Module-
 * private — consumers go through `getAppInsights()` so the SDK handle is not
 * a mutable public binding (a `let` export leaks the live reference past
 * every wrapper, which defeats the no-op-when-unconfigured invariant).
 */
let _appInsights: ApplicationInsights | null = null;

/**
 * Accessor for the live AI client (or `null` when AI is unconfigured).
 * Features that need to opt into AI-specific behaviour (page-view tracking,
 * custom metrics) beyond the helpers below go through this accessor; tests
 * use it to assert the init pathway constructed the SDK.
 */
export function getAppInsights(): ApplicationInsights | null {
  return _appInsights;
}

let initialised = false;
let handlersInstalled = false;

/**
 * Test-only injection seam for the `ApplicationInsights` class. Vitest's
 * `vi.mock` cannot reliably swap the SDK's named export (the package ships a
 * UMD `main` bundle + an ESM `module` bundle; the import graph resolves to
 * the UMD form at test time, which Vitest's transformer treats as opaque),
 * so we accept a factory the tests can override before calling
 * `initAppInsights()`. Default is the real class; spec files reassign via
 * `_setApplicationInsightsCtorForTests`.
 */
type AICtor = new (
  snippet: ConstructorParameters<typeof ApplicationInsights>[0],
) => ApplicationInsights;
let applicationInsightsCtor: AICtor = ApplicationInsights;

/**
 * Resolve the SPA-side telemetry sampling percentage from
 * `VITE_APPLICATIONINSIGHTS_SAMPLING_PCT`. Defaults to 100 (capture
 * everything) when unset or unparseable, and clamps to [1, 100] so a
 * fat-fingered `0` or `500` can't silently drop all telemetry or be rejected
 * by the SDK.
 *
 * IMPORTANT — this is SDK-side (pre-send) sampling and is ONLY the first of
 * two multiplicative stages. SPA telemetry is ALSO subject to the App
 * Insights component's ingestion sampling on the resource. Effective capture
 * is `samplingPercentage * ingestionSampling`, so setting 100 here is
 * necessary-but-not-sufficient for full capture — the resource's adaptive /
 * ingestion sampling must also be pinned to 100 to retain every envelope.
 */
function resolveSamplingPercentage(): number {
  const raw = import.meta.env.VITE_APPLICATIONINSIGHTS_SAMPLING_PCT;
  const parsed = Number(raw);
  if (raw === undefined || raw === '' || Number.isNaN(parsed)) return 100;
  return Math.min(100, Math.max(1, parsed));
}

/**
 * Derive the API host(s) we want correlation headers attached to. Same-origin
 * deployments (SWA Linked Backend in prod) need no special handling; explicit
 * `VITE_API_BASE` overrides take precedence for the dev split-origin setup.
 */
function resolveCorrelationDomains(): string[] {
  const apiBase = import.meta.env.VITE_API_BASE;
  if (apiBase) {
    try {
      return [new URL(apiBase).host];
    } catch {
      // Malformed env value — fall through to same-origin assumption rather
      // than blowing up the bundle at init time.
    }
  }
  return [window.location.host];
}

/**
 * Initialise the SDK. Safe to call multiple times — subsequent invocations
 * are silent no-ops. Returns the live client when AI is enabled, `null`
 * otherwise.
 */
export function initAppInsights(): ApplicationInsights | null {
  if (initialised) return _appInsights;
  initialised = true;

  const connectionString = import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING;
  if (!connectionString) {
    // Production builds without a conn string are a misconfig — surface it
    // once via a structured console.warn. Dev / test stay silent. We do NOT
    // install the global error/unhandledrejection handlers in this branch:
    // without an AI sink they would silently swallow errors that would
    // otherwise surface via the browser's default console reporting.
    notifyTelemetryDisabled(
      'VITE_APPLICATIONINSIGHTS_CONNECTION_STRING missing — SPA telemetry disabled',
    );
    return null;
  }

  const ai = new applicationInsightsCtor({
    config: {
      connectionString,
      extensions: [reactPlugin],
      // T6 — distributed-trace correlation. W3C mode emits + accepts
      // `traceparent` / `tracestate`; the API's auto-instrumented OTel
      // already honours these, so an SPA `exceptions.operation_Id` will
      // chain back to the originating API `requests.operation_Id`.
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      distributedTracingMode: DistributedTracingModes.W3C,
      correlationHeaderDomains: resolveCorrelationDomains(),
      // Keep the SDK's default exclude list (Microsoft endpoints, etc.) by
      // not overriding `correlationHeaderExcludedDomains`.
      disableFetchTracking: false,
      // SDK-driven page-view tracking — every route change emits a `pageViews`
      // envelope. The `pushRoute` call in `App.tsx` is unaffected: it only
      // maintains the breadcrumb ring buffer for exception context, not page
      // views.
      enableAutoRouteTracking: true,
      // SPA-side (pre-send) sampling knob. See `resolveSamplingPercentage`
      // for why this is necessary-but-not-sufficient for full capture.
      samplingPercentage: resolveSamplingPercentage(),
      // Disable the SDK's built-in window.error / unhandledrejection capture.
      // Our manual handlers below own this surface and attach breadcrumbs;
      // leaving the SDK auto-capture on shipped duplicate exceptions envelopes
      // (closes C4 from review). Note the asymmetric key names — the SDK's
      // IConfiguration uses `disableExceptionTracking` (a `disable*` boolean)
      // but `enableUnhandledPromiseRejectionTracking` (an `enable*` boolean).
      disableExceptionTracking: true,
      enableUnhandledPromiseRejectionTracking: false,
    },
  });
  ai.loadAppInsights();

  // Stamp cloud.role + application.ver via a telemetry initializer so the
  // tag survives across all envelope types (exceptions, traces, metrics).
  ai.addTelemetryInitializer((item: ITelemetryItem) => {
    item.tags = item.tags ?? {};
    item.tags['ai.cloud.role'] = 'marketplace-ui';
    item.tags['ai.application.ver'] = __APP_VERSION__ ?? 'dev';
  });

  _appInsights = ai;
  installGlobalHandlersOnce();

  // One-time boot canary. Proves the telemetry pipeline is live end-to-end
  // independent of organic traffic — if this event lands in App Insights, the
  // conn string, SDK init, and ingestion are all healthy. No-ops when AI is
  // unconfigured (but we're past the `connectionString` guard here, so it
  // always fires on a successful init).
  trackEvent('telemetry_canary', { tier: 'frontend', appVersion: __APP_VERSION__ ?? 'dev' });
  return ai;
}

function installGlobalHandlersOnce(): void {
  if (handlersInstalled) return;
  if (typeof window === 'undefined') return;
  handlersInstalled = true;

  window.addEventListener('error', (ev) => {
    const err = ev.error instanceof Error ? ev.error : new Error(ev.message);
    trackException(err, {
      source: 'window.error',
      filename: ev.filename,
      lineno: ev.lineno,
    });
  });

  window.addEventListener('unhandledrejection', (ev) => {
    const reason: unknown = ev.reason;
    const err = reason instanceof Error ? reason : new Error(String(reason));
    trackException(err, { source: 'unhandledrejection' });
  });
}

/**
 * Track an exception with optional custom dimensions. When AI is unconfigured
 * (local dev, no conn string), this is a no-op — every call site can fire
 * unconditionally without env-aware branching.
 *
 * Attaches `properties.breadcrumbs` (serialised route + fetch ring buffers)
 * with a 4 KB cap: oldest entries are dropped from each buffer until the
 * payload fits.
 */
export function trackException(error: Error, properties?: Record<string, unknown>): void {
  if (!_appInsights) return;
  const crumbs = serialiseBreadcrumbs();
  // When both buffers are empty, omit the `breadcrumbs` key entirely rather
  // than shipping `''` — the AI portal's custom-dimension filters treat empty
  // strings as present, which polluted the breadcrumb column on routine
  // exceptions thrown before any nav/fetch had occurred.
  _appInsights.trackException(
    { exception: error },
    { ...properties, ...(crumbs && { breadcrumbs: crumbs }) },
  );
}

/**
 * Track a custom event. No-op when AI is unconfigured. Pass-through to the
 * underlying SDK; we keep the wrapper so call sites don't import the SDK
 * directly (cleaner mocks, single chokepoint for cross-cutting concerns).
 */
export function trackEvent(name: string, properties?: Record<string, unknown>): void {
  if (!_appInsights) return;
  _appInsights.trackEvent({ name }, properties);
}

/**
 * Serialise the breadcrumb snapshot, dropping oldest entries from both
 * buffers until the JSON payload is under the configured byte cap. Returns
 * `''` when both buffers are empty (so the AI dimension is omitted rather
 * than shipping `"{}"` on every exception).
 *
 * Cap rationale: AI custom dimensions have a 8 KB per-property soft limit
 * (per the documented envelope schema), but the SDK applies extra encoding
 * overhead. 4 KB keeps us comfortably inside both the limit and the
 * portal's display truncation threshold.
 */
function serialiseBreadcrumbs(): string {
  const snap = snapshot();
  if (snap.routes.length === 0 && snap.fetches.length === 0) return '';

  let payload = JSON.stringify(snap);
  while (payload.length > BREADCRUMB_CHAR_CAP) {
    // Drop oldest from whichever buffer is longer; if tied, drop a route
    // (page navigation is usually less diagnostic than the fetch chain).
    if (snap.routes.length === 0 && snap.fetches.length === 0) break;
    if (snap.routes.length >= snap.fetches.length && snap.routes.length > 0) {
      snap.routes.shift();
    } else {
      snap.fetches.shift();
    }
    payload = JSON.stringify(snap);
  }
  return payload;
}

/**
 * Test-only reset. Not exported through the public surface; spec files import
 * this directly when they need to exercise the init-twice / no-env paths.
 */
export function _resetAppInsightsForTests(): void {
  _appInsights = null;
  initialised = false;
  handlersInstalled = false;
  applicationInsightsCtor = ApplicationInsights;
}

/**
 * Test-only constructor override. See the `AICtor` declaration above for the
 * rationale. Spec files reassign this before calling `initAppInsights()`.
 */
export function _setApplicationInsightsCtorForTests(ctor: AICtor): void {
  applicationInsightsCtor = ctor;
}
